import { User } from '@/types/userRole';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface Manager {
  id: string;
  name: string;
  email: string;
  role: 'manager';
  status: 'active' | 'inactive';
  createdAt: Date;
  permissions: string[];
}

export interface ManagerInvite {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'accepted' | 'expired' | 'canceled';
  expiresAt: Date;
  createdAt: Date;
  businessId: string;
}

// Managers are represented by users granted the `admin` role via user_roles.
export const getManagers = async (_instanceId?: string): Promise<Manager[]> => {
  try {
    const { data: roles, error } = await supabase
      .from('user_roles')
      .select('user_id, role, created_at')
      .eq('role', 'admin');
    if (error) throw error;
    const ids = (roles ?? []).map((r) => r.user_id);
    if (ids.length === 0) return [];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, email, first_name, last_name')
      .in('id', ids);
    const map = new Map<string, any>();
    (profiles ?? []).forEach((p) => map.set(p.id, p));
    return (roles ?? []).map((r) => {
      const p = map.get(r.user_id) ?? {};
      return {
        id: r.user_id,
        name:
          [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || 'Manager',
        email: p.email ?? '',
        role: 'manager',
        status: 'active',
        createdAt: new Date(r.created_at),
        permissions: ['manage_users', 'view_reports'],
      };
    });
  } catch (e) {
    logger.error('getManagers failed', e);
    return [];
  }
};

// Invites are not persisted yet — return empty until an invitations table is added.
export const getPendingInvites = async (_instanceId?: string): Promise<ManagerInvite[]> => {
  return [];
};

export const inviteManager = async (
  name: string,
  email: string,
): Promise<ManagerInvite> => {
  // Send an invitation email; persistence to an `invitations` table is a future enhancement.
  try {
    await supabase.functions.invoke('send-email', {
      body: {
        type: 'manager_invite',
        to: email,
        payload: { name },
      },
    });
  } catch (e) {
    logger.error('inviteManager email failed', e);
  }
  return {
    id: `invite-${Date.now()}`,
    name,
    email,
    status: 'pending',
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    createdAt: new Date(),
    businessId: 'current',
  };
};

export const cancelInvite = async (_inviteId: string): Promise<boolean> => true;

export const deactivateManager = async (managerId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('user_roles')
    .delete()
    .eq('user_id', managerId)
    .eq('role', 'admin');
  if (error) {
    logger.error('deactivateManager failed', error);
    return false;
  }
  return true;
};

export const reactivateManager = async (managerId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('user_roles')
    .upsert({ user_id: managerId, role: 'admin' }, { onConflict: 'user_id,role' });
  if (error) {
    logger.error('reactivateManager failed', error);
    return false;
  }
  return true;
};

export const convertManagerToUser = (manager: Manager): User => ({
  ...manager,
  id: manager.id,
  name: manager.name,
  email: manager.email,
  role: 'manager',
  createdAt: manager.createdAt,
  isMasterAdmin: false,
  isAdmin: true,
  isSuperAdmin: false,
});
